import React from 'react';
import {
    Box,
    Drawer,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Avatar,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import Assets from './Assets';
import { BucketURL } from '../../ApiSetup/Api';
import {
  getAdminPath,
  getIconPath,
  isMenuPathActive,
} from '../../Utils/adminNavigation';

const SECTION_LABELS = {
    "DASHBOARD": "Dashboard",
    "REGISTRATION": "Registration",
    "MEMBERS": "Members",
    "ORGANISATION": "Organisation",
    "FINANCE": "Finance",
    "EXPENSES": "Expenses",
    "CLAIMS": "Claims",
    "SUPPORT": "Support",
    "POST_GENERATOR": "Post Generator",
    "SETTINGS": "Settings",
};

const MobileDrawerMenu = ({
    open,
    onClose,
    groupedPages,
    sectionOrder,
    user,
    onLogout,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavigation = (path) => {
        navigate(getAdminPath(path));
        onClose();
    };

    const isActive = (path) => isMenuPathActive(location.pathname, path);

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            sx={{ zIndex: 1300 }}
            PaperProps={{
                sx: {
                    borderRadius: '20px 20px 0 0',
                    maxHeight: '100vh',
                    height: 'auto',
                    backgroundColor: '#ffffff',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 20px',
                    borderBottom: '1px solid #e2e8f0',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#ffffff',
                    zIndex: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {user?.avtar ? (
                        <Avatar
                            src={BucketURL + user?.avtar}
                            alt="Profile"
                            sx={{ width: 44, height: 44 }}
                        />
                    ) : (
                        <Avatar sx={{ width: 44, height: 44, bgcolor: '#3E4094' }}>
                            {user?.fullName?.charAt(0) || 'U'}
                        </Avatar>
                    )}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1e293b',
                            }}
                        >
                            {user?.fullName || 'User'}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                color: '#64748b',
                            }}
                        >
                            {user?.loginRole || 'Admin'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        backgroundColor: '#f1f5f9',
                        minWidth: 44,
                        minHeight: 44,
                        '@media (hover: hover)': {
                            '&:hover': { backgroundColor: '#e2e8f0' },
                        },
                    }}
                >
                    <CloseIcon sx={{ color: '#64748b' }} />
                </IconButton>
            </Box>

            {/* Drag Handle */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: '#e2e8f0',
                }}
            />

            {/* Menu Content */}
            <Box
                sx={{
                    overflowY: 'auto',
                    padding: '8px 12px 24px',
                    paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
                }}
            >
                {sectionOrder?.map((section, sectionIndex) => {
                    const sectionPages = groupedPages?.[section] || [];
                    if (sectionPages.length === 0) return null;

                    return (
                        <Box key={section} sx={{ mt: sectionIndex > 0 ? 2 : 0 }}>
                            {/* Section Label */}
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '8px 12px 4px',
                                }}
                            >
                                {SECTION_LABELS[section] || section}
                            </Typography>

                            {/* Section Items */}
                            <List sx={{ padding: 0 }}>
                                {sectionPages.map((menu, index) => {
                                    const active = isActive(menu?.path);
                                    return (
                                        <ListItem key={menu?.pageName || index} disablePadding sx={{ mb: 0.5 }}>
                                            <ListItemButton
                                                onClick={() => handleNavigation(menu?.path)}
                                                sx={{
                                                    borderRadius: '12px',
                                                    padding: '12px 16px',
                                                    backgroundColor: active ? '#3E4094' : 'transparent',
                                                    '@media (hover: hover)': {
                                                        '&:hover': {
                                                            backgroundColor: active ? '#363785' : '#f1f5f9',
                                                        },
                                                    },
                                                    '&:active': {
                                                        transform: 'scale(0.98)',
                                                    },
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 40,
                                                        color: active ? '#ffffff' : '#64748b',
                                                    }}
                                                >
                                                    <Assets
                                                        src={getIconPath(menu)}
                                                        height="22px"
                                                        width="22px"
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={menu?.pageName}
                                                    primaryTypographyProps={{
                                                        fontSize: '14px',
                                                        fontWeight: active ? 600 : 500,
                                                        color: active ? '#ffffff' : '#1e293b',
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    );
                })}

                {/* Logout Button */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={onLogout}
                            sx={{
                                borderRadius: '12px',
                                padding: '12px 16px',
                                backgroundColor: '#fef2f2',
                                '@media (hover: hover)': {
                                    '&:hover': {
                                        backgroundColor: '#fee2e2',
                                    },
                                },
                                '&:active': {
                                    transform: 'scale(0.98)',
                                    backgroundColor: '#fee2e2',
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <LogoutIcon sx={{ color: '#ef4444' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Logout"
                                primaryTypographyProps={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#ef4444',
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                </Box>
            </Box>
        </Drawer>
    );
};

export default MobileDrawerMenu;
